import React, { useState } from 'react';
import { FiCheckCircle, FiRefreshCw, FiGlobe, FiEye, FiEyeOff, FiPlus, FiCheck, FiX, FiChevronDown, FiCode, FiDownload, FiAlertCircle, FiMail, FiSend } from 'react-icons/fi';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import { toast } from '../../Toast';
import { CopyButton, CodeBox, useWidgetSite } from './shared';

const SITE_KEY = 'pk_live_49M_8f2c1a7d';
const SECRET_MASKED = 'sk_live_••••••••••••••••3f9a';
const SECRET_FULL = 'sk_live_4be81d0f2a9c7e6b11d83f9a';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// sample traffic (no API log exists yet)
const REQUESTS: { when: string; from: string; what: string; refused?: boolean }[] = [
    { when: '2 minutes ago', from: '49metals.com.au', what: 'announcements · 200 OK' },
    { when: '2 minutes ago', from: '49metals.com.au', what: 'share-price · 200 OK' },
    { when: '14 minutes ago', from: 'www.49metals.com.au', what: 'top-holders · 200 OK' },
    { when: '1 hour ago', from: 'unknown-site.example', what: 'refused — domain not allowed', refused: true },
    { when: 'Yesterday, 11:02 pm', from: 'server (secret key)', what: 'registry sync · 200 OK' },
];

const PLUGIN_STEPS = [
    'In WordPress go to Plugins › Add new, search for “Investi”, then Install and Activate.',
    'Open Investi › Settings in the WordPress menu.',
    'Paste your Site key and press Save. The plugin loads the script, the stylesheet and the chart library for you.',
    'In Investi › Settings, tick “Use Investi styling” to load the Investi stylesheet, or untick it if your developer is styling the widgets to match your theme.',
    'Add a widget to any page with the Investi block, or with a shortcode such as [investi_market_data].',
];

// the tags the plugin adds for you (investi.php): stylesheet, echarts for the chart, and the script with the site key
const EMBED = `<link rel="stylesheet" href="https://api.investi.com.au/investi-widget.css" />
<script src="https://cdn.jsdelivr.net/npm/echarts@5.6.0/dist/echarts.min.js"></script>
<script src="https://api.investi.com.au/investi.js?apiKey=${SITE_KEY}" defer></script>`;

// Widgets › step 1 (spec 626:5750)
const WidgetsConnect: React.FC = () => {
    const { host } = useWidgetSite();
    const [domains, setDomains] = useState([host, `www.${host}`, `staging.${host}`]);
    const [adding, setAdding] = useState(false);
    const [newDomain, setNewDomain] = useState('');
    const [revealed, setRevealed] = useState(false);
    const [testing, setTesting] = useState(false);
    // which install way is open: the plugin (default) or pasting the code
    const [way, setWay] = useState<'plugin' | 'code' | null>('plugin');
    const [useStyling, setUseStyling] = useState(true);
    const [loadCharts, setLoadCharts] = useState(true);
    const [devEmail, setDevEmail] = useState('');

    const test = () => {
        setTesting(true);
        setTimeout(() => {
            setTesting(false);
            toast.info(`Preview only: ${host} wasn’t contacted`);
        }, 1200);
    };

    const addDomain = () => {
        const d = newDomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(d)) {
            toast.error('Enter a domain such as www.example.com.au');
            return;
        }
        if (!domains.includes(d)) setDomains(ds => [...ds, d]);
        setNewDomain('');
        setAdding(false);
        toast.info('Preview only: the domain was not saved');
    };

    const send = () => {
        toast.info(`Preview only: no instructions were sent to ${devEmail.trim()}`);
        setDevEmail('');
    };

    return <>
        <div className="wd-status">
            <span className="icon-tile"><FiCheckCircle /></span>
            <div className="text">
                <strong>Connected and working</strong>
                <span>Last request 2 minutes ago from {host} · 4,120 requests in the last 24 hours · no errors</span>
            </div>
            <button type="button" className="btn-outline with-icon" onClick={test} disabled={testing}>
                <FiRefreshCw className={testing ? 'spin' : ''} /> {testing ? 'Testing…' : 'Test the Connection'}
            </button>
        </div>

        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><ShieldCheckIcon /></span>
                <div className="text">
                    <h2>Step 1 — connect your website to Investi</h2>
                    <div className="hint">Your website talks to Investi through the API. Two keys: a site key that lives in the page and only works on your domains, and a secret key that never leaves your server.</div>
                </div>
                <span className="badge ok">Connected</span>
            </div>

            <div className="wd-keys">
                <div className="key">
                    <div className="h"><FiGlobe /> <strong>Site key</strong></div>
                    <p>Goes in your website. Safe to be seen — it only works on the domains you list below.</p>
                    <div className="value"><code>{SITE_KEY}</code><CopyButton text={SITE_KEY} what="site key" /></div>
                    <p className="muted">Read‑only. It can show your published content and nothing else.</p>
                </div>
                <div className="key secret">
                    <div className="h"><ShieldCheckIcon /> <strong>Secret key</strong></div>
                    <p>For your server only. Never put this in a page, a theme file or an email.</p>
                    <div className="value">
                        <code>{revealed ? SECRET_FULL : SECRET_MASKED}</code>
                        <span className="actions">
                            <button type="button" className="wd-copy" onClick={() => setRevealed(!revealed)}>{revealed ? <FiEyeOff /> : <FiEye />} {revealed ? 'Hide' : 'Reveal'}</button>
                            <button type="button" className="wd-copy" onClick={() => toast.info('Preview only: the secret key was not rotated')}><FiRefreshCw /> Rotate</button>
                        </span>
                    </div>
                    <p className="dark">Shown once when created. Last used 2 minutes ago · created 12 Aug 2026 by Wallace Turner.</p>
                </div>
            </div>

            <div className="wd-domains">
                <div className="h">
                    <FiGlobe /> <strong>Allowed domains</strong>
                    <button type="button" className="wd-copy" onClick={() => setAdding(true)} disabled={adding}><FiPlus /> Add a Domain</button>
                </div>
                {domains.map(d => <div key={d} className="domain">
                    <FiCheck className="tick" />
                    <code>{d}</code>
                    <span className="muted">verified</span>
                    <button type="button" className="remove" aria-label={`Remove ${d}`} onClick={() => setDomains(ds => ds.filter(x => x !== d))}><FiX /></button>
                </div>)}
                {adding && <div className="domain adding">
                    <input value={newDomain} placeholder="www.example.com.au" onChange={e => setNewDomain(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addDomain(); }} autoFocus />
                    <button type="button" className="wd-copy" onClick={addDomain}>Add</button>
                    <button type="button" className="wd-copy" onClick={() => { setAdding(false); setNewDomain(''); }}>Cancel</button>
                </div>}
            </div>

            <div className="wd-note"><ShieldCheckIcon /> <p>Requests from anywhere else are refused, so a copied site key on another website will not work. Rotating a key takes effect straight away — tell your developer before you rotate the secret key.</p></div>

            <div className="wd-ways">
                <h3>Two Ways to Put This on the Website</h3>

                <div className={`way${way === 'plugin' ? ' open' : ''}`}>
                    <button type="button" className="way-head" aria-expanded={way === 'plugin'} onClick={() => setWay(way === 'plugin' ? null : 'plugin')}>
                        <span className="icon-tile"><FiGlobe /></span>
                        <span className="t"><strong>The Investi WordPress Plugin</strong><span>Recommended · no code at all</span></span>
                        <span className="badge easiest">Easiest</span>
                        <FiChevronDown className="chev" />
                    </button>
                    {way === 'plugin' && <div className="way-body">
                        {PLUGIN_STEPS.map((s, i) => <div key={i} className="step"><span className="n">{i + 1}</span><p>{s}</p></div>)}
                        <div className="wp-settings">
                            <div className="label">In Investi › Settings</div>
                            <label className="opt">
                                <input type="checkbox" checked={useStyling} onChange={e => setUseStyling(e.target.checked)} />
                                <span className="t"><strong>Use Investi styling</strong><span>Ticked: the Investi stylesheet loads and the widgets look finished straight away. This is the default.</span></span>
                            </label>
                            <label className="opt">
                                <input type="checkbox" checked={loadCharts} onChange={e => setLoadCharts(e.target.checked)} />
                                <span className="t"><strong>Load the chart library</strong><span>Ticked: adds echarts, needed only if you use the share price chart.</span></span>
                            </label>
                            <div className="wd-note warn"><FiAlertCircle /> <p>Untick “Use Investi styling” only if your developer is writing their own CSS. The widgets still work, but they arrive unstyled and will inherit your theme.</p></div>
                        </div>
                        <div className="label">Your site key</div>
                        <div className="site-key"><code>{SITE_KEY}</code><CopyButton text={SITE_KEY} what="site key" /></div>
                        <p className="muted">Paste this into Investi › Settings in WordPress. It is the site key, not the secret key — it only reads your published content.</p>
                        <button type="button" className="wd-copy download" onClick={() => toast.info('Preview only: the plugin download isn’t connected yet')}><FiDownload /> Download the Plugin</button>
                    </div>}
                </div>

                <div className={`way${way === 'code' ? ' open' : ''}`}>
                    <button type="button" className="way-head" aria-expanded={way === 'code'} onClick={() => setWay(way === 'code' ? null : 'code')}>
                        <span className="icon-tile"><FiCode /></span>
                        <span className="t"><strong>Paste the Code Yourself</strong><span>For any website, or a developer</span></span>
                        {way === 'code' ? <FiChevronDown className="chev" /> : <FiPlus className="chev" />}
                    </button>
                    {way === 'code' && <div className="way-body">
                        <p className="muted">Put these three lines in the website’s <code className="inline">&lt;head&gt;</code>, once, on every page. Leave out the echarts line if you don’t use the share price chart.</p>
                        <CodeBox code={EMBED} dark />
                        <p className="muted">Then add a widget anywhere with its code from step 2, Widgets.</p>
                    </div>}
                </div>
            </div>
        </section>

        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiMail /></span>
                <div className="text">
                    <h2>Send it to a developer</h2>
                    <div className="hint">Investi emails the site key and the embed code — never the secret key. The developer gets a link that expires in 48 hours.</div>
                </div>
            </div>
            <div className="wd-send">
                <input type="email" value={devEmail} placeholder="developer@example.com" onChange={e => setDevEmail(e.target.value)} />
                <button type="button" className="btn-primary with-icon" onClick={send} disabled={!EMAIL.test(devEmail.trim())}><FiSend /> Send the Instructions</button>
            </div>
        </section>

        <section className="card">
            <h2 className="card-title">Recent API activity</h2>
            <div className="wd-requests" tabIndex={0} role="region" aria-label="Recent API activity">
                {REQUESTS.map((r, i) => <div key={i} className={r.refused ? 'refused' : ''}>
                    {r.refused ? <FiAlertCircle /> : <FiCheck />}
                    <span className="when">{r.when}</span>
                    <code>{r.from.replace('49metals.com.au', host)}</code>
                    <span className="what">{r.what}</span>
                </div>)}
            </div>
            <p className="muted">A refused request means someone used your site key on a domain that is not on your list. Nothing was exposed.</p>
        </section>
    </>;
};

export default WidgetsConnect;
